import { create } from 'zustand'
import { api, describeError, setUnauthenticatedHandler } from '@/services/api'
import { env } from '@/lib/env'
import { clearAuthToken, setAuthToken } from '@/lib/authToken'
import type { PersonRole } from '@/types'

export interface SignInConfig {
  turnstileEnabled: boolean
  turnstileSiteKey: string
  /**
   * Whether the sign-in form will accept a MyMDV email and password.
   *
   * Replaced `ssoEnabled` / `ssoLabel` on 2026-10-26, when Ian had the "Sign
   * in with MyMDV" button removed — the form checks a MyMDV password against
   * Hub itself now, so the trip to Hub and back had nothing left to do. This
   * flag only words the line above the form; it gates no behaviour.
   */
  hubLoginEnabled?: boolean
}

export interface AuthUser {
  id: string
  firstName: string
  lastName: string
  email: string
  /* `PersonRole`, not a copy of it. This was its own inline union, so adding
     `viewer` to the real type left this one behind and TypeScript narrowed
     `role` to 'member' in usePermissions — the compiler caught a divergence
     that would otherwise have been a signed-in viewer treated as a member. */
  role: PersonRole
  avatarColor: string
  avatarUrl?: string | null
  mustChangePassword: boolean
  notifyEmail: boolean
  lastLoginAt?: string | null
}

interface AuthState {
  /** `unknown` until the session has been checked — guards must not flash. */
  status: 'unknown' | 'authenticated' | 'anonymous' | 'setup'
  user: AuthUser | null
  error: string | null
  /** A server-side blocker (e.g. a migration that has not been run yet). */
  serverMessage: string | null
  /** Public sign-in configuration, served rather than baked into the build. */
  signInConfig: SignInConfig | null
  busy: boolean

  check: () => Promise<void>
  loadSignInConfig: () => Promise<void>
  login: (email: string, password: string, turnstileToken?: string | null) => Promise<boolean>
  setup: (input: {
    email: string
    password: string
    firstName: string
    lastName: string
    turnstileToken?: string | null
  }) => Promise<boolean>
  logout: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>
  setNotifyEmail: (enabled: boolean) => Promise<void>
  setAvatarUrl: (avatarUrl: string | null) => void
  completeSso: (code: string) => Promise<boolean>
  /** Picks up an existing MyMDV session without showing anybody a page. */
  clearError: () => void
}

interface MeResponse {
  authenticated: boolean
  needsSetup: boolean
  user: AuthUser | null
}

/**
 * In mock mode there is no server to authenticate against, so the app runs
 * open — the same way it did before accounts existed. Every real deployment
 * (VITE_USE_MOCK_API=false) is gated.
 */
const MOCK_USER: AuthUser = {
  id: 'person_ian',
  firstName: 'Local',
  lastName: 'Preview',
  email: 'preview@localhost',
  role: 'admin',
  avatarColor: '#15803d',
  mustChangePassword: false,
  notifyEmail: false,
}

/** Guards against a burst of 401s each firing its own verification. */
let verifying = false

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'unknown',
  user: null,
  error: null,
  serverMessage: null,
  signInConfig: null,
  busy: false,

  async check() {
    /*
     * A 401 usually means the session really has gone — but not always, and
     * throwing someone back to sign-in the instant one call fails produced a
     * login that appeared to work and then bounced. Ask the server first, and
     * only tear down if it agrees we are anonymous.
     */
    setUnauthenticatedHandler(() => {
      if (env.useMockApi || get().status !== 'authenticated' || verifying) return
      verifying = true
      void api
        .get<MeResponse>('/auth/me')
        .then((me) => {
          if (!me.authenticated) {
            clearAuthToken()
            set({ status: 'anonymous', user: null })
          }
        })
        .catch(() => {
          // Could not ask — leave the session alone rather than guess.
        })
        .finally(() => {
          verifying = false
        })
    })

    if (env.useMockApi) {
      set({ status: 'authenticated', user: MOCK_USER })
      return
    }
    try {
      const me = await api.get<MeResponse>('/auth/me')
      if (me.authenticated && me.user) set({ status: 'authenticated', user: me.user, error: null })
      else set({ status: me.needsSetup ? 'setup' : 'anonymous', user: null })
    } catch (checkError) {
      // Usually just "not signed in" — but a server that cannot answer at all
      // needs to say why, or the sign-in screen is a dead end.
      const message = describeError(checkError)
      set({
        status: 'anonymous',
        user: null,
        serverMessage: /migration|database|not configured/i.test(message) ? message : null,
      })
    }
  },

  async loadSignInConfig() {
    if (env.useMockApi) {
      set({ signInConfig: { turnstileEnabled: false, turnstileSiteKey: '', hubLoginEnabled: false } })
      return
    }
    try {
      set({
        signInConfig: await api.get<SignInConfig>(
          '/auth/config',
        ),
      })
    } catch {
      // An older server has no such endpoint — carry on without the widget
      // rather than blocking sign-in entirely.
      set({ signInConfig: { turnstileEnabled: false, turnstileSiteKey: '', hubLoginEnabled: false } })
    }
  },

  async login(email, password, turnstileToken) {
    set({ busy: true, error: null })
    try {
      const result = await api.post<{ user: AuthUser; token?: string }>('/auth/login', {
        email,
        password,
        turnstileToken,
      })
      // Stored before any further call, so the very next request is
      // authenticated even if the session cookie did not stick.
      if (result.token) setAuthToken(result.token)
      set({ status: 'authenticated', user: result.user })
      return true
    } catch (loginError) {
      set({ error: describeError(loginError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  async setup(input) {
    set({ busy: true, error: null })
    try {
      const result = await api.post<{ user: AuthUser; token?: string }>('/auth/setup', input)
      if (result.token) setAuthToken(result.token)
      set({ status: 'authenticated', user: result.user })
      return true
    } catch (setupError) {
      set({ error: describeError(setupError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Even if the call fails, drop the local session.
    }
    clearAuthToken()
    set({ status: 'anonymous', user: null })
  },

  async changePassword(currentPassword, newPassword) {
    set({ busy: true, error: null })
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword })
      const user = get().user
      if (user) set({ user: { ...user, mustChangePassword: false } })
      return true
    } catch (changeError) {
      set({ error: describeError(changeError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  async setNotifyEmail(enabled) {
    const user = get().user
    if (!user) return
    set({ user: { ...user, notifyEmail: enabled } })
    try {
      await api.patch('/auth/preferences', { notifyEmail: enabled })
    } catch (prefError) {
      set({ user: { ...user, notifyEmail: !enabled }, error: describeError(prefError) })
    }
  },

  setAvatarUrl(avatarUrl) {
    const user = get().user
    if (user) set({ user: { ...user, avatarUrl } })
  },

  /**
   * Finishes a "Sign in with MyMDV" round trip.
   *
   * The provider hands the browser back with a single-use code in the URL,
   * never a real token — a token in a redirect ends up in browser history and
   * the server access log. This trades it for the real thing over POST.
   */
  async completeSso(code) {
    set({ busy: true, error: null })
    try {
      const result = await api.post<{ user: AuthUser; token?: string }>('/auth/oidc/exchange', { code })
      if (result.token) setAuthToken(result.token)
      set({ status: 'authenticated', user: result.user })
      return true
    } catch (ssoError) {
      set({ error: describeError(ssoError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  clearError() {
    set({ error: null })
  },
}))
